/**
 * Registry of per-menu explainer content + onboarding-tour steps.
 *
 * Keys are URL path prefixes; `getMenuInfoForPath()` does a longest-prefix
 * match. To add a new menu, append one entry — the shell's PageInfoBanner
 * and MenuTour will pick it up automatically for every route under that
 * prefix. No changes required inside individual MFEs.
 *
 * NOTE: `/pcf/*` is intentionally omitted because pcf-mfe ships its own
 * richer InfoBanner + Tour inside every page. Adding a shell entry would
 * double up. If PCF's in-MFE tour is ever retired, add an entry here.
 */

export interface MenuTourStep {
  title: string;
  body: string;
  cta?: { label: string; href: string };
}

export interface MenuInfoEntry {
  /** Stable key used for localStorage (tour completion, banner open state). */
  key: string;
  /** URL path prefix that owns this menu (e.g. "/lca"). */
  pathPrefix: string;
  /**
   * Additional URL prefixes that should resolve to the same entry.
   * Useful when the same page is reachable at multiple URLs (e.g. Org Setup
   * lives at both `/settings/org_setup` and `/settings/team`). Longest match
   * across all prefixes wins.
   */
  pathAliases?: string[];
  /** Display name shown in the info banner + tour header. */
  title: string;
  /**
   * Render the "About this page" banner as a small right-aligned "Replay tour ›"
   * control whose details open in a compact box on the right, instead of the
   * full-width bar. Opt-in per menu; pages without it keep the full bar.
   */
  compact?: boolean;
  /** "What is this workspace?" — 1–2 sentences. */
  description?: string;
  /** How work moves through this module. */
  workflow?: string;
  /** Why the current page exists / when to use it. Optional. */
  purpose?: string;
  /** First-visit tour steps. Omit or leave empty to disable the tour. */
  tourSteps?: MenuTourStep[];
}

export const MENU_INFO: MenuInfoEntry[] = [
  // ── Life Cycle Assessment ──────────────────────────────────────────────
  {
    key: 'lca',
    pathPrefix: '/lca',
    title: 'Life Cycle Assessment',
    description:
      'Model the full environmental profile of a product or process across raw materials, manufacture, distribution, use and end-of-life — following ISO 14040/14044.',
    workflow:
      'Define goal & scope → build the product system (unit processes + flows) → run LCIA (impact assessment) → interpret hotspots → publish EPD-ready reports.',
    tourSteps: [
      {
        title: 'Welcome to LCA',
        body: 'Model the full environmental profile of a product or process using ISO 14040/14044 methodology. Six pointers, then dive in.',
      },
      {
        title: '1 · Goal & scope',
        body: 'Every study starts by declaring the functional unit, system boundary, allocation rules and impact categories.',
        cta: { label: 'Set up a study', href: '/lca/studies' },
      },
      {
        title: '2 · Build the product system',
        body: 'Add unit processes and flows. Reuse foreground data across studies; link to background datasets (ecoinvent, ILCD) via factors.',
        cta: { label: 'Open product systems', href: '/lca/systems' },
      },
      {
        title: '3 · Run LCIA',
        body: 'Pick a method (ReCiPe, CML, EF 3.1) and compute impacts. Results are versioned so you can compare scenarios.',
        cta: { label: 'Open the calculator', href: '/lca/calculate' },
      },
      {
        title: '4 · Interpret hotspots',
        body: 'Contribution analysis + sensitivity checks show which processes drive impact. Feed findings into design decisions.',
        cta: { label: 'View results', href: '/lca/results' },
      },
      {
        title: '5 · Reports & EPD',
        body: 'Publish study reports, EPD extracts and third-party review packages directly from the interpretation view.',
        cta: { label: 'Open reports', href: '/lca/reports' },
      },
    ],
  },

  // ── Carbon Accounting ───────────────────────────────────────────────────
  {
    key: 'carbon',
    pathPrefix: '/carbon',
    pathAliases: [
      '/carbon-emissions',
      '/carbon_emissions',
      '/carbon_emission',
      '/data_collection/carbon_emission',
      '/data_collection/carbon-emissions',
      '/data-collection/carbon_emission',
      '/data-collection/carbon-emissions',
    ],
    title: 'GHG Emissions',
    description:
      "Track and manage your organization's greenhouse-gas emissions across Scope 1, 2 and 3, aligned to the GHG Protocol.",
    workflow:
      'Pick a GHG database → set the reporting frequency → enter Scope 1 / Scope 2 / Scope 3 activity data → sync CE formulas → review totals and export.',
    tourSteps: [
      {
        title: 'Step 1 · Pick a GHG database',
        body: 'Top-right, choose the emission-factor database that fits your region (DEFRA, EPA, IPCC, etc.). Every calculation uses factors from the selected database.',
      },
      {
        title: 'Step 2 · Set frequency',
        body: 'Click "Set Frequency" to declare how often you capture data — monthly, quarterly or annually. Reports and reminders follow this cadence.',
      },
      {
        title: 'Step 3 · Enter Scope 1 / 2 / 3 data',
        body: 'Use the Scope tabs and sub-tabs (Stationary, Mobile, Fugitive, Biogenic for Scope 1). Fill in activity data — the emission factor auto-populates from the chosen database.',
      },
      {
        title: 'Step 4 · Add custom factors (optional)',
        body: 'Missing a factor? "Add Custom Factor" lets you register a bespoke one for your tenant. Custom factors sit alongside DB factors in the picker.',
      },
      {
        title: 'Step 5 · Sync CE values',
        body: 'When framework-question answers reference CE formulas, hit "Sync CE Values" to recalculate everything against the latest activity data.',
      },
    ],
  },

  // ── Compliance › ESG Assurance ──────────────────────────────────────────
  // More specific than `/compliance` so it wins longest-prefix match on
  // /compliance/assurance URLs. Covers the standalone /assurance alias too.
  {
    key: 'compliance-assurance',
    pathPrefix: '/compliance/assurance',
    pathAliases: ['/assurance', '/esg-assurance', '/esg_assurance'],
    title: 'ESG Assurance',
    description:
      'Manage independent assurance engagements over your ESG disclosures — reasonable or limited assurance against ISAE 3000/3410, ISSA 5000, AA1000AS or ASAE 3410, with a full audit trail per engagement.',
    workflow:
      'Create an engagement → set standard + scope → pick KPIs → declare objective & level → assess risk → collect evidence → run environmental / social / governance procedures → design controls + sampling → assemble documentation → export audit trail.',
    tourSteps: [
      {
        title: 'Welcome to ESG Assurance',
        body: 'The reviewer/auditor workspace. Every engagement moves through a fixed 10-step assurance workflow — the tabs at the top mirror those steps.',
      },
      {
        title: '1 · New engagement',
        body: 'Click "New Engagement" (top-right) to create one. Give it a name, pick the assurance standard (ISAE 3000, ISSA 5000, AA1000AS, ASAE 3410) and the level (limited vs reasonable).',
      },
      {
        title: '2 · Scope & KPIs',
        body: 'Under Standard & Scope + Scope — KPIs, declare what disclosures are in scope. KPIs are pulled from your subscribed frameworks (BRSR, CSRD, GRI, etc.).',
      },
      {
        title: '3 · Risk + evidence',
        body: 'Risk Assessment scores each in-scope KPI. Evidence lets you upload/link the source documents the assurance opinion will rest on.',
      },
      {
        title: '4 · Procedures',
        body: 'Environmental / Social / Governance tabs are where you execute the assurance procedures and record findings. Controls + Sampling document the design of the work.',
      },
      {
        title: '5 · Documentation + audit trail',
        body: 'The Documentation tab assembles the final report; Audit Trail is the immutable log of every state change, ready for reviewer QC and regulator inspection.',
      },
      {
        title: 'Notifications',
        body: 'The bell in the header flashes when a new engagement is created — no need to keep this page open.',
      },
    ],
  },

  // ── Compliance (CSRD / ESRS / EU Taxonomy / Climate) ────────────────────
  // ── Compliance › Materiality Assessment (double materiality) ────────────
  // More specific than `/compliance` so it wins longest-prefix match. Covers
  // every URL variant the sidebar / DB menu seeds might link to.
  {
    key: 'compliance-materiality',
    pathPrefix: '/compliance/materiality',
    pathAliases: [
      '/materiality',
      '/materiality_assessment',
      '/materiality-assessment',
      '/assessment/materiality',
      '/assessment/materiality_assessment',
      '/assessment/materiality-assessment',
    ],
    title: 'Materiality Assessment',
    description:
      'Run a CSRD/ESRS-compliant double materiality assessment — score Impacts, Risks and Opportunities (IROs) on impact + financial materiality to identify which topics unlock topical disclosures.',
    workflow:
      'Create a project → add topics/IROs → score impact → score financial → set thresholds → compute results → link governance + publish CSRD report / board deck.',
    tourSteps: [
      {
        title: 'Step 1 · Create Project',
        body: 'Click "New Project" (top-right). Give it a name and pick the reporting period. Every subsequent step is scoped to the active project.',
      },
      {
        title: 'Step 2 · Add Topics / IROs',
        body: 'Open the Topic Library tab. Load ESRS default topics or add custom ones. Under each topic, add IROs (Impacts, Risks, Opportunities) — those are what you actually score.',
      },
      {
        title: 'Step 3 · Score Impact',
        body: 'Score every IRO on scale, scope, irremediability, likelihood. Use Stakeholder Engagement to invite external stakeholders (via survey link) to contribute scores.',
      },
      {
        title: 'Step 4 · Score Financial',
        body: 'For each IRO, score financial magnitude + likelihood — how much does this topic move revenue, cost or capital access. This step is only shown in Double mode.',
      },
      {
        title: 'Step 5 · Set Thresholds',
        body: 'Thresholds decide what counts as material. Defaults follow EFRAG guidance; adjust on the Thresholds tab if you have a defensible reason. Set Industry Avg to benchmark against peers.',
      },
      {
        title: 'Step 6 · Compute Results',
        body: 'Click "Compute Results" to run the calc. The Materiality Matrix + Material Topics count update, and every material topic unlocks its topical disclosures in CSRD/ESRS. Export via CSRD Report or Board Deck.',
      },
    ],
  },

    // ── Water & Effluent Compliance (India — CPCB / SPCB / NGT / CGWA) ──────
  // Sits under /water/*. Pages live in compliance-mfe but the parent menu is
  // seeded independently via super-admin's WaterMenuSeedService.
  {
    key: 'water-compliance',
    pathPrefix: '/water',
    pathAliases: [
      '/water_compliance',
      '/water-compliance',
      '/water/setup',
      '/water/consents',
      '/water/monitoring',
      '/water/operations',
      '/water/events',
      '/water/portfolio',
    ],
    title: 'Water & Effluent Compliance',
    description:
      'Keep every facility compliant with Indian water and wastewater regulations — CPCB Schedule VI, SPCB consents (CTE/CTO), CGWA NOCs, NGT orders, ULB permits — with one place for consents, limits, monitoring and events.',
    workflow:
      'Set up a site (sector + receiving body) → auto-map CPCB Schedule VI limits → register CTE/CTO/NOCs → capture lab / OCEMS / flow data → risk engine flags breaches → CAPA tasks close the loop.',
    tourSteps: [
      {
        title: 'Step 1 · Create a site',
        body: 'Sites & Setup → Add site. Pick the sector (textile, pharma, sugar, …) and CPCB category. State + river basin drive later NGT/CGWA lookups.',
      },
      {
        title: 'Step 2 · Add discharge outlets',
        body: 'For each outlet, pick the receiving body (inland surface / sewer / land irrigation / marine / in-plant reuse). CPCB Schedule VI general standards materialize automatically as per-outlet limits.',
      },
      {
        title: 'Step 3 · Upload consents (Phase 2)',
        body: 'Consents & NOCs — attach your CTE/CTO/CGWA-NOC/ULB permits. Renewal reminders fire at 180/90/30 days; special conditions become trackable checklists.',
      },
      {
        title: 'Step 4 · Capture monitoring data',
        body: 'Monitoring — enter lab results, upload NABL CoAs, feed OCEMS data. Every reading is checked against the outlet\'s limit set the moment it arrives.',
      },
      {
        title: 'Step 5 · Log daily operations',
        body: 'Operations — ETP/STP shift log: flows, pH, DO, MLSS, chemical dosages, sludge dispatch, incidents. Mobile-friendly for on-site operators.',
      },
      {
        title: 'Step 6 · Watch Events & Risk',
        body: 'The risk engine cross-checks lab + OCEMS + flow against all limits and OCEMS uptime rules — every breach becomes an Event with severity + suggested CAPA + legal-hook mapping (Water Act, EP Act, SPCB consent clause).',
      },
      {
        title: 'Step 7 · Ship audit-ready packs',
        body: 'When SPCB inspects, one click assembles the last 12 months\' consents + monitoring + events + CAPAs into an Inspection Pack. Form V exports are pre-filled from the same data.',
      },
    ],
  },

  {
    key: 'compliance',
    pathPrefix: '/compliance',
    title: 'Compliance',
    description:
      'Sustainability disclosure obligations — CSRD/ESRS, AASB S1/S2, IFRS S2, TCFD and EU Taxonomy — in one workspace.',
    workflow:
      'Run double materiality → answer topical disclosures gated by your material topics → attach evidence → assemble the assurance-ready report.',
    tourSteps: [
      {
        title: 'Welcome to Compliance',
        body: 'The regulator-facing side of the platform. Everything here feeds the annual sustainability statement.',
      },
      {
        title: '1 · Double materiality',
        body: 'Score IROs on impact and financial materiality. Only material topics unlock topical ESRS/AASB questions.',
        cta: { label: 'Open materiality', href: '/compliance/materiality' },
      },
      {
        title: '2 · Topical disclosures',
        body: 'ESRS 2 is always mandatory. E1–E5, S1–S4 and G1 appear when their topic is material.',
        cta: { label: 'CSRD workspace', href: '/compliance/csrd' },
      },
      {
        title: '3 · Climate disclosures',
        body: 'The four-pillar TCFD/AASB-S2 workflow — governance, strategy, risk management, metrics & targets.',
        cta: { label: 'Climate disclosures', href: '/compliance/climate' },
      },
      {
        title: '4 · EU Taxonomy',
        body: 'Assess eligible activities against SC + DNSH + minimum safeguards; the workspace computes turnover/capex/opex alignment ratios.',
        cta: { label: 'EU Taxonomy', href: '/compliance/eu-taxonomy' },
      },
    ],
  },

  // ── Framework Questions ─────────────────────────────────────────────────
  // Named Framework Questions throughout: the legacy path segments
  // (/fillings, /filings) are kept as aliases below, but no user-visible
  // string calls it Filings any more.
  // Data-collection workspace where the tenant answers framework-specific
  // KPI questions (BRSR, GRI, CSRD, etc.) and admins assign questions to
  // team members. Two tabs: Data Collection and My Assignments.
  {
    key: 'framework-questions',
    compact: true,
    pathPrefix: '/data_collection/fillings',
    pathAliases: ['/data-collection/fillings', '/framework-questions', '/framework_questions', '/fillings', '/filings'],
    title: 'Framework Questions',
    description:
      'Answer framework-specific KPI questions (BRSR, GRI, CSRD, TCFD, etc.) and route work to the right owners.',
    workflow:
      'Pick a framework → filter by department → assign questions to teammates → answer + attach evidence → progress bars roll up per topic and per framework.',
    tourSteps: [
      {
        title: 'Two tabs, one workspace',
        body: 'Data Collection is the full question tree for every framework you use. My Assignments is the personal view — only the questions routed to you.',
      },
      {
        title: 'Filter what you see',
        body: 'Use the Framework, Department and Search filters at the top to narrow the tree. Search matches topic names, KPI names and question text.',
      },
      {
        title: 'Assign questions',
        body: 'Click Assign Questions (top right) to enter selection mode, tick the KPIs your team owns, then bulk-assign / reassign / unassign in one step.',
      },
      {
        title: 'Answer + evidence',
        body: 'Each question has a value field, an optional evidence upload, and a location/frequency picker. Progress % updates the moment you save.',
      },
      {
        title: 'My Assignments',
        body: 'Switch to My Assignments to focus on your own queue — the same questions, filtered to just what you own.',
      },
    ],
  },

  // ── Data Reviews (Pending Reviews + Review History) ─────────────────────
  // Reviewer workspace where managers approve/reject teammates' submitted
  // answers before they roll up into the framework report.
  {
    key: 'data-reviews',
    pathPrefix: '/data_reviews',
    pathAliases: [
      '/data-reviews',
      '/pending-reviews', '/pending_reviews',
      '/review-history', '/review_history',
      '/reviews-listing', '/reviews_listing',
    ],
    title: 'Data Reviews',
    description:
      'Review submissions from your team. Accept answers that are ready for reporting, reject ones that need rework, and keep a full history of every review decision.',
    workflow:
      'Assignee submits an answer → it appears in Pending Reviews → reviewer inspects the value + evidence → Accept (locks it) or Reject (returns it with a note) → outcome is written to Review History.',
    tourSteps: [
      {
        title: 'Two views, one workflow',
        body: 'Pending Reviews is your queue of open submissions. Review History is the audit trail of everything you (and your peers) have already decided.',
      },
      {
        title: 'Top stats at a glance',
        body: 'Pending, Accepted Today, Rejected Today, Total Reviewed — the four counters tell you your reviewer load and today’s throughput without opening a submission.',
      },
      {
        title: 'Search the queue',
        body: 'Filter by question text, framework, topic or KPI name. Useful when a specific disclosure is blocking your report.',
      },
      {
        title: 'Accept or Reject',
        body: 'Open a submission → check the value + attached evidence → Accept locks the answer, Reject sends it back with a note to the assignee.',
      },
      {
        title: 'Notifications',
        body: 'The bell in the header will flash when new submissions land in your queue — no need to keep this page open.',
      },
    ],
  },

  // ── Reporting ───────────────────────────────────────────────────────────
  {
    key: 'reporting',
    pathPrefix: '/reporting',
    title: 'Reporting',
    description:
      'Generate stakeholder-ready sustainability reports — PDF (pdfmake), Word (Docxtemplater) and XBRL — from the data captured across the platform.',
    workflow:
      'Pick a template → map sections to underlying answers → preview → export.',
    tourSteps: [
      {
        title: 'Welcome to Reporting',
        body: 'Turn captured data into board-ready documents. Templates are versioned per framework.',
      },
      {
        title: '1 · Template library',
        body: 'CSRD, TCFD, GRI, BRSR, custom — each with pre-mapped placeholders that pull from your live answers.',
      },
      {
        title: '2 · Preview & export',
        body: 'Preview the assembled report before export. XBRL is available for filings that require it.',
      },
    ],
  },

  // ── Analytics ───────────────────────────────────────────────────────────
  {
    key: 'analytics',
    pathPrefix: '/analytics',
    title: 'Analytics',
    description:
      'Cross-cutting dashboards over your ESG data — trends, benchmarks and target progress.',
    workflow:
      'Choose a lens (emissions, water, waste, targets) → filter by year / entity → drill into the underlying answer.',
    tourSteps: [
      {
        title: 'Welcome to Analytics',
        body: 'Read-only dashboards over data you capture elsewhere. Every metric is drillable back to its source answer.',
      },
      {
        title: '1 · Targets',
        body: 'SBTi-aligned target tracking with automatic status roll-ups.',
        cta: { label: 'View targets', href: '/analytics/targets' },
      },
      {
        title: '2 · Peer benchmarks',
        body: 'Anonymised sector percentiles — see where you stand without exposing individual peers.',
      },
    ],
  },

  // ── ESG Data ────────────────────────────────────────────────────────────
  {
    key: 'esg-data',
    pathPrefix: '/collections',
    title: 'ESG Data',
    description:
      'The single system of record for every data point captured across the platform. Answers live here; frameworks pull from these values.',
    workflow:
      'Categories organise the taxonomy → answers are collected against questions → assignments route unanswered questions to owners.',
    tourSteps: [
      {
        title: 'Welcome to ESG Data',
        body: 'Everything you capture across compliance, carbon, LCA and reporting is normalised into ESG categories here.',
      },
      {
        title: '1 · Categories',
        body: 'Browse the ESG taxonomy — environment, social, governance and cross-cutting.',
      },
      {
        title: '2 · Banking parameters',
        body: 'Sector-specific data points used by finance-facing frameworks (PCAF, TNFD).',
      },
    ],
  },

  // ── Sustainability › ESG Roadmap ────────────────────────────────────────
  // More specific than `/sustainability` so it wins longest-prefix match on
  // the roadmap route. Aliases cover every URL variant the sidebar or DB
  // menu seeds might link to.
  {
    key: 'esg-roadmap',
    pathPrefix: '/strategic_roadmap',
    pathAliases: [
      '/strategic-roadmap',
      '/sustainability/esg-roadmap',
      '/sustainability/esg_roadmap',
      '/sustainability/roadmap',
      '/sustainability/strategic_roadmap',
      '/sustainability/strategic-roadmap',
      '/esg-roadmap',
      '/esg_roadmap',
      '/esg_strategic_roadmap',
      '/esg-strategic-roadmap',
      '/roadmap',
    ],
    title: 'ESG Strategic Roadmap',
    description:
      'Phased ESG action plan spanning short (0–12 months), medium (1–3 years) and long (3–5 years) horizons. Assign owners, set targets, track progress, and steer the whole tenant toward its net-zero / social / governance commitments.',
    workflow:
      'Pick a horizon → add initiatives per pillar (Environmental / Social / Governance) → assign owner + target → progress rolls up automatically from underlying KPIs → status flips (On Track / At Risk / Completed) → export the plan for board / regulator review.',
    tourSteps: [
      {
        title: 'Welcome to ESG Roadmap',
        body: 'The strategic-planning workspace. Every action your team commits to across E, S and G lives here — with an owner, a deadline and a live progress bar.',
      },
      {
        title: 'Top KPIs',
        body: 'The five tiles at the top — Total, On Track, At Risk, Completed, Overall Progress — are your at-a-glance portfolio KPIs. They update as underlying initiatives change status.',
      },
      {
        title: 'Timeline · Board · Summary',
        body: 'Timeline groups initiatives by horizon (Short / Medium / Long). Board is a Kanban by status. Summary is the executive rollup — same data, three views for three audiences.',
      },
      {
        title: 'Parameters + KPI Master',
        body: 'Parameters lets you tie each initiative to an underlying question or CE parameter so progress auto-computes. KPI Master is the library of measurable KPIs you can attach.',
      },
      {
        title: 'Net Zero Benchmark',
        body: 'Compare your emission trajectory against science-based / 1.5°C pathways. Shows how far you are from a compliant net-zero glide-path.',
      },
      {
        title: 'Add an initiative',
        body: 'Click the "+" button (top-right) to add a new initiative. Fill in pillar, horizon, owner, target and start/end dates — the initiative shows up on Timeline and Board immediately.',
      },
      {
        title: 'Search + notifications',
        body: 'The search bar filters the Timeline view by initiative name, owner or category. The header bell will light up when new initiatives are added — no need to keep this page open.',
      },
    ],
  },

  // ── Sustainability ─────────────────────────────────────────────────────
  {
    key: 'sustainability',
    pathPrefix: '/sustainability',
    title: 'Sustainability',
    description:
      'Forward-looking planning — net-zero pathways, emission modelling and reforestation / RCO offset accounting.',
    workflow:
      'Baseline → model a scenario → set targets → track abatement projects → recognise offsets.',
    tourSteps: [
      {
        title: 'Welcome to Sustainability',
        body: 'The place to plan reductions — not just measure them. Feeds Analytics for progress tracking.',
      },
      {
        title: '1 · Net-zero pathway',
        body: 'Build a science-based trajectory from your baseline. Multiple scenarios can co-exist for what-if planning.',
      },
      {
        title: '2 · Abatement projects',
        body: 'Track individual reduction projects with expected vs. realised savings.',
      },
      {
        title: '3 · RCO / carbon plantations',
        body: 'Register reforestation-based offsets with plot-level vintage tracking.',
      },
    ],
  },

  // ── Supplier ────────────────────────────────────────────────────────────
  {
    key: 'supplier',
    pathPrefix: '/supplier',
    title: 'Supplier',
    description:
      'Assess, engage and score your value-chain partners on ESG performance. Feeds Scope 3 in Carbon and S2/S4 in Compliance.',
    workflow:
      'Register suppliers → launch assessments → collect responses → score → surface risks + engagement actions.',
    tourSteps: [
      {
        title: 'Welcome to Supplier',
        body: 'Your value-chain workspace — assessments, engagement, scoring.',
      },
      {
        title: '1 · Assessments',
        body: 'Build custom questionnaires or use library templates. Send to suppliers with due dates + reminders.',
      },
      {
        title: '2 · Engagement',
        body: 'Track responses, follow up on gaps, and surface high-risk suppliers for review.',
      },
    ],
  },

  // ── Chat ────────────────────────────────────────────────────────────────
  {
    key: 'chat',
    pathPrefix: '/chat',
    title: 'ESG Chat',
    description:
      'Ask questions about your ESG data in natural language — powered by OpenAI gpt-4o-mini.',
    workflow:
      'Type a question → the assistant grounds its answer in your captured data → cited sources link back to the underlying record.',
    tourSteps: [
      {
        title: 'Welcome to ESG Chat',
        body: 'Ask about emissions, disclosures, targets — the assistant answers using your live data.',
      },
      {
        title: 'Tips',
        body: 'Be specific. "What were our Scope 2 emissions in FY24 for the AU region?" beats "How are we doing?".',
      },
    ],
  },

  // ── Settings › Org Setup (Roles + Locations + Team Members + Org Chart) ──
  // Covers every URL the sidebar may open this page at.
  {
    key: 'settings-org-setup',
    // Compact banner: this page carries its own title, search and summary strip
    // in the header, so a full-width "About this page" bar above them pushed the
    // real content down. Opt-in here only — other menus keep the full bar.
    compact: true,
    pathPrefix: '/settings/org_setup',
    pathAliases: [
      '/settings/org-setup',
      '/settings/team',
      '/settings/team_permission',
      '/settings/term_permission',
      '/org_setup',
      '/org-setup',
      '/team',
    ],
    title: 'Org Setup',
    description:
      'Set up the people side of your tenant — roles, locations, invited team members and the reporting hierarchy that drives approvals.',
    workflow:
      'Create roles → add locations → invite users and assign them a role + location → arrange the org chart.',
    tourSteps: [
      {
        title: 'Step 1 · Create Roles',
        body: 'Open the Roles tab and click "Create Role". Give the role a name, then tick the menu items and features it should access. Every user you invite later must be assigned one of these roles.',
      },
      {
        title: 'Step 2 · Add Locations',
        body: 'Switch to the Locations tab and register every site, office, plant or facility. Team members and emissions data are scoped by location — nothing works without at least one location.',
      },
      {
        title: 'Step 3 · Invite Team Members',
        body: 'Go to the Team Members tab and invite users by email. Pick a role (step 1) and one or more locations (step 2) for each invite. Invited users appear under the "Invited" sub-tab until they accept.',
      },
      {
        title: 'Step 4 · Build the Org Chart',
        body: 'On the Org Setup tab, drag members into a reporting hierarchy — who reports to whom. This chart drives approval flows and manager visibility across the platform.',
      },
    ],
  },

  // ── Settings › Profiles (Profile + General tabs) ────────────────────────
  // More specific than `/settings` so getMenuInfoForPath wins longest-prefix
  // match on /settings/company_profile URLs.
  {
    key: 'settings-profiles',
    pathPrefix: '/settings/company_profile',
    pathAliases: ['/settings/company-profile', '/company_profile', '/company-profile', '/profiles'],
    title: 'Profiles',
    description:
      "Your personal profile and your organization's general information — split across two tabs.",
    workflow:
      'Profile tab → keep your personal info and avatar current. General tab → update the organization details everyone in the tenant sees.',
    tourSteps: [
      {
        title: 'Profile tab',
        body: 'Your personal information — name, email, employee ID, job title, department and avatar. Click Edit on the right to change any field, then Save.',
      },
      {
        title: 'General tab',
        body: "Your organization's information — company name, logo, address and contact details. Changes here are visible to every user in your tenant.",
      },
    ],
  },

  // ── Settings ────────────────────────────────────────────────────────────
  {
    key: 'settings',
    pathPrefix: '/settings',
    title: 'Settings',
    description:
      'Organisation profile, users, roles, financial years, integrations and preferences.',
    workflow:
      'Set the org profile → invite users + assign roles → configure FY periods → connect integrations.',
    tourSteps: [
      {
        title: 'Welcome to Settings',
        body: 'The admin surface. Most changes here affect everyone in your tenant.',
      },
      {
        title: 'Users & roles',
        body: 'Invite team members and assign role-based access. Roles are gated by menu.',
      },
      {
        title: 'Financial year',
        body: 'The FY selector in the header respects the periods you configure here.',
      },
    ],
  },

  // ── ROC (Registrar of Companies compliance) ─────────────────────────────
  {
    key: 'roc',
    pathPrefix: '/roc',
    title: 'ROC Compliance',
    description:
      'Corporate secretarial compliance — directors, auditors, statutory forms and filing deadlines.',
    workflow:
      'Maintain the register → tasks pick up filing deadlines from statutory calendars → complete + file.',
    tourSteps: [
      {
        title: 'Welcome to ROC',
        body: 'The corporate-secretarial workspace. Deadlines are calendar-driven and generate tasks automatically.',
      },
      {
        title: 'Registers',
        body: 'Directors, auditors, KMPs — the source of truth for who signs what.',
      },
      {
        title: 'Forms & tasks',
        body: 'Every filing has a form template and a task. Overdue items surface on the dashboard.',
      },
    ],
  },

  // ── Waste & Compliance ──────────────────────────────────────────────────
  {
    key: 'waste-compliance',
    pathPrefix: '/waste',
    pathAliases: [
      '/waste-compliance',
      '/waste_compliance',
      '/waste-management',
      '/waste_management',
      '/wastecompliance',
      '/wastemanagement',
    ],
    title: 'Waste & Compliance',
    description:
      "Track every waste stream your facilities generate (Hazardous, Plastic, E-Waste, BMW, C&D, Battery, MSW) with full chain-of-custody from segregation → storage → transport → disposal, aligned to India's HW/SWM/PWM/E-Waste/BMW/C&D/Battery Rules + CPCB/SPCB consents.",
    workflow:
      'Register your sites & CTE/CTO consents → define waste streams (mapped to the rule schedule) → onboard authorised vendors (TSDF/CBWTF/recyclers) → dispatch waste via digital manifests → monitor effluent + air emissions → file periodic returns (Form 3/4, BMW annual, EPR) → hand a full audit pack to SPCB inspectors.',
    tourSteps: [
      {
        title: 'Step 1 · Dashboard',
        body: 'The tiles up top show waste streams, consents, vendors, manifests and consents expiring within 90 days at a glance.',
      },
      {
        title: 'Step 2 · Waste Streams',
        body: 'Register every waste stream with a short code (HW01, PW02, EW03…) and map it to the applicable rule category. Mark streams that count toward EPR (plastic packaging, e-waste, batteries).',
      },
      {
        title: 'Step 3 · Consents',
        body: 'Upload your CTE / CTO / HW Authorization / BMW Authorization / EPR registrations with validity dates. The dashboard flags anything expiring in the next 180 / 90 / 30 days.',
      },
      {
        title: 'Step 4 · Vendors',
        body: 'Onboard the authorised transporters, TSDFs, recyclers and CBWTFs you dispatch waste to. Upload their CPCB authorization certificates — the platform will block dispatches to expired or unauthorised vendors.',
      },
      {
        title: 'Step 5 · Manifests',
        body: 'Create a digital manifest per dispatch (mirrors Form 10 for hazardous waste). The workflow — Draft → Approved → Dispatched → Vendor Acknowledged → Closed — keeps the full chain of custody for SPCB audits.',
      },
      {
        title: 'Coming next',
        body: 'Sprint 2 adds segregation checklists (mobile-friendly for the shop floor), storage capacity + max-hold-days alerts, and monitoring plans for effluent/air with threshold-breach incidents. EPR portal exports land in Sprint 5.',
      },
    ],
  },

  // ── Green Finance › Green Loan Book ─────────────────────────────────────
  // ── Green Certification Hub ──────────────────────────────────────────────
  // More specific than `/green-finance` so it wins longest-prefix match.
  {
    key: 'green-certification',
    pathPrefix: '/green_certification',
    pathAliases: [
      '/green-certification',
      '/greencertification',
      '/green_cert',
      '/green-cert',
      '/certification_hub',
      '/certification-hub',
      '/igbc',
      '/green-finance/green_certification',
      '/green-finance/green-certification',
    ],
    title: 'Green Certification Hub',
    description:
      'Manage IGBC / LEED / GRIHA building certifications for every location — memberships, projects (by rating system + target level), point-level requirements, meetings with the certification body and query threads — in one place.',
    workflow:
      'Register memberships (IGBC/LEED/GRIHA) → create a project per building with rating system + target level → track credit-level requirements → schedule meetings + actions with the certification body → log & respond to queries → export project scorecards.',
    tourSteps: [
      {
        title: 'Step 1 · Dashboard',
        body: 'The first tab is your portfolio at a glance — total projects, certified, in progress, expiring soon, plus a breakdown by stage (Planning / Documentation / Submitted / Under Review / Certified / Expired).',
      },
      {
        title: 'Step 2 · Memberships',
        body: 'Register your organization\'s memberships with certification bodies (IGBC, USGBC-LEED, GRIHA Council). Memberships unlock the projects you can file under each body.',
      },
      {
        title: 'Step 3 · Projects',
        body: 'One project per building/location. Pick the rating system (LEED BD+C, IGBC Green New Buildings, GRIHA, etc.), target level (Certified/Silver/Gold/Platinum), consultant contact, and target date. Projects drive every other tab.',
      },
      {
        title: 'Step 4 · Requirements',
        body: 'For an active project, the Requirements tab lists every credit + prerequisite (grouped by category — Sustainable Sites, Water Efficiency, Energy, Materials, IEQ, Innovation). Mark each as Not Started / In Progress / Completed and attach evidence.',
      },
      {
        title: 'Step 5 · Meetings & Actions',
        body: 'Schedule design-review calls with the certification body. Every meeting can have follow-up Actions assigned to team members with due dates.',
      },
      {
        title: 'Step 6 · Queries',
        body: 'The back-and-forth thread with the certification-body reviewer. Log queries received during review, link them to the affected requirement, respond, and close them out.',
      },
    ],
  },

  // More specific than `/green-finance` so it wins longest-prefix match on
  // the loan-book route. Covers the standalone /green-loan-book alias too.
  {
    key: 'green-loan-book',
    pathPrefix: '/green-finance/green-loan-book',
    pathAliases: [
      '/green-finance/green_loan_book',
      '/green_finance/green-loan-book',
      '/green_finance/green_loan_book',
      '/green-loan-book',
      '/green_loan_book',
    ],
    title: 'Green Loan Book',
    description:
      'Classify and monitor your loan portfolio against the RBI/SEBI green taxonomy — decide which exposures count as Green, Transition, Neutral or Brown, track PSL-eligible green lending, and generate use-of-proceeds reports.',
    workflow:
      'Load the taxonomy → register loan products → classify each loan (Green / Transition / Neutral / Brown / Unclassified) → review sector exposures → export the summary report to disclosure and treasury teams.',
    tourSteps: [
      {
        title: 'Welcome to Green Loan Book',
        body: 'The banking-side workspace. Every loan gets a taxonomy classification, and the portfolio-level KPIs (Green %, Transition, PSL Green) roll up from those individual decisions.',
      },
      {
        title: 'Dashboard',
        body: 'The eight tiles at the top are your at-a-glance portfolio KPIs — total book, green amount, green %, transition, brown, unclassified and PSL-eligible green. Numbers update as you classify loans.',
      },
      {
        title: 'Taxonomy',
        body: 'The RBI/SEBI green-taxonomy tree. Each pillar → category → sub-activity defines what qualifies. Use it as your source of truth when classifying.',
      },
      {
        title: 'Loan Products',
        body: 'Register every loan on the book. Fill in borrower, sector, product type and amount, then pick the taxonomy category — the loan flows into the correct classification bucket automatically.',
      },
      {
        title: 'Sector Exposure',
        body: 'Concentration analysis across sectors, split by classification. Use it to spot brown-heavy sectors that could become stranded assets, or under-served green sectors.',
      },
      {
        title: 'Reports',
        body: 'One-click classification summary report — the PDF you send to CFO / disclosure / RBI filings. Sourced from the exact same numbers as the dashboard.',
      },
      {
        title: 'Search + notifications',
        body: 'The search bar (top-right) filters loans by name, borrower or sector. The header bell will light up whenever new loans are added to the book — no need to keep this page open.',
      },
    ],
  },

  // ── Green Finance ───────────────────────────────────────────────────────
  {
    key: 'green-finance',
    pathPrefix: '/green-finance',
    title: 'Green Finance',
    description:
      'Green certifications register and green-loan book — the intersection of sustainability performance and financing.',
    workflow:
      'Log certifications → tie them to activities → tag loans → produce use-of-proceeds reports.',
    tourSteps: [
      {
        title: 'Welcome to Green Finance',
        body: 'Where sustainability performance meets financing. Feeds green-bond and use-of-proceeds reporting.',
      },
      {
        title: 'Certifications',
        body: 'Log every green certification (LEED, GRESB, EDGE, etc.) with expiry tracking.',
      },
      {
        title: 'Loan book',
        body: 'Tag loans against eligible activities. The taxonomy alignment ratios roll up automatically.',
      },
    ],
  },

  // ── Dashboard (top-level landing) ───────────────────────────────────────
  {
    key: 'dashboard',
    pathPrefix: '/dashboard',
    title: 'Dashboard',
    description:
      'Your daily landing page — status of assignments, disclosures in flight, and open notifications.',
    workflow: 'Open items → drill into the workspace that owns them.',
  },
];

/**
 * Longest-prefix match against the pathname. `/lca/studies/123` resolves to
 * the `/lca` entry.
 */
export function getMenuInfoForPath(pathname: string): MenuInfoEntry | null {
  let best: MenuInfoEntry | null = null;
  let bestLen = 0;
  for (const entry of MENU_INFO) {
    const prefixes = [entry.pathPrefix, ...(entry.pathAliases ?? [])];
    for (const p of prefixes) {
      if (pathname === p || pathname.startsWith(p + '/')) {
        if (p.length > bestLen) {
          best = entry;
          bestLen = p.length;
        }
      }
    }
  }
  return best;
}


/**
 * Label of the deepest allowed menu whose path is a prefix of `pathname`,
 * i.e. the name configured in super-admin for the page the user is on.
 * Used so the About box / tour / page headings follow super-admin renames
 * instead of the static `title` in this file.
 */
export function findAllowedMenuLabel(
  menus: Array<{ label?: string; customLabel?: string | null; path?: string; children?: unknown[] }>,
  pathname: string,
): string | null {
  const norm = (v: string) => v.toLowerCase().replace(/\/+$/, '');
  const target = norm(pathname || '');
  let best: { label: string; len: number } | null = null;
  const walk = (list: Array<{ label?: string; customLabel?: string | null; path?: string; children?: unknown[] }>) => {
    list.forEach((m) => {
      const p = m.path ? norm(m.path.startsWith('/') ? m.path : `/${m.path}`) : '';
      // A tenant's own name (Profile → Menu Setup) wins over the super-admin label.
      const shown = m.customLabel?.trim() || m.label;
      if (p && shown && (target === p || target.startsWith(`${p}/`)) && (!best || p.length > best.len)) best = { label: shown, len: p.length };
      if (Array.isArray(m.children) && m.children.length) walk(m.children as Array<{ label?: string; customLabel?: string | null; path?: string; children?: unknown[] }>);
    });
  };
  walk(menus);
  return best ? best.label : null;
}
